import { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useActions } from 'statux'
import Form from '../Form/Form'
import TextArea from '../TextArea/TextArea'
import Rating from '../Rating/Rating'

import { fetchClient } from '../../utils/fetch'
import { getQueryParams } from '../../utils/location'
import { graphqlClient, gql } from '../../utils/gql'

export default function RegistrationForm (props = {
    id: null,
    role: null,
    submitBtnText: null
}) {
    const role = props.role || 'member'

    const id = props.id || `registration-form-${role}`

    const advisorRatings = [{
        name: 'rating_business_plan_strategy',
        label: 'Business plan / Strategy'
    }, {
        name: 'rating_sales_marketing',
        label: 'Sales / Marketing'
    }, {
        name: 'rating_fundraising',
        label: 'Fundraising'
    }, {
        name: 'rating_technology_development',
        label: 'Technology development'
    }, {
        name: 'rating_venture_structuring',
        label: 'Venture structuring'
    }]

    const textareaMaxLength = 500

    const history = useHistory()

    const setUser = useActions('user')

    const submitMsg = useRef()

    const [submissionState, setSubmissionState] = useState({
        success: null,
        message: null,
        token: null,
        refresh: null,
        user: null
    })

    const isDisabled = () => submissionState.success === true

    const onSubmit = async (e) => {
        const formData = new window.FormData(e.target)

        const data = Array.from(formData.entries())
            .reduce((acc, curr) => {
                const isDetailsText = curr[0]
                    .match(/^(qualifications|vision)$/)

                const isDetailsRating = curr[0]
                    .match(/^rating_/)

                const isDetails = isDetailsText || isDetailsRating

                if (isDetails)
                    acc.details[`${curr[0]}`] = isDetailsRating
                        ? Number(curr[1])
                        : curr[1]

                if (!isDetails)
                    acc[`${curr[0]}`] = curr[0] === 'approved'
                        ? Boolean(curr[1])
                        : curr[1]

                return acc
            }, { details: {} })

        const { details, ...profileData } = data

        const registerOptions = {}

        if (profileData.role === 'member') {
            registerOptions.default_role = 'member'
            registerOptions.allowed_roles = ['me', 'member', 'user']
        }

        const res = await fetchClient
            .post('/auth/register', {
                json: {
                    email: profileData.email,
                    user_data: {
                        display_name: `${profileData.first_name} ${
                                profileData.last_name}`
                            .trim(),
                        profile: {
                            data: profileData
                        }
                    },
                    register_options: registerOptions
                }
            })
            .catch(err => err)

        const success = Boolean(res.jwt_token)

        let message = res.message
            || 'An unknown error has occurred.'

        if (success) {
            message = `${role === 'member'
                ? 'Application'
                : 'Registration'} successfully submitted`

            await graphqlClient
                .request(gql`
                    mutation UpdateProfileDetails ($details: jsonb) {
                        update_profiles_by_pk (
                            pk_columns: { email: "${res.user.email}" },
                            _set: {
                                details: $details
                            }
                        ) {
                            details
                        }
                    }
                `, {
                    details: details
                }, {
                    headers: {
                        authorization: `Bearer ${res.jwt_token}`,
                        'x-hasura-role': 'me',
                        'x-hasura-user-id': res.user.id
                    }
                })
                .catch(err => console.warn(err))
        }

        setSubmissionState({
            token: res.jwt_token,
            refresh: res.refresh_token,
            user: res.user,
            success,
            message
        })
    }

    useEffect(() => {
        if (submitMsg.current
                && submissionState.success)
            submitMsg.current.focus()
    }, [submitMsg, submissionState])

    useEffect(() => {
        if (submissionState.success === null)
            return

        if (!submissionState.success
                && submitMsg.current)
            submitMsg.current.focus()

        if (!submissionState.success)
            return

        setUser(Object.assign(submissionState.user, {
            avatar: null
        }))

        const advancedMembershipLandingPathname
            = '/advanced-membership'

        const isAdvancedMembershipCheckout
            = role === 'member'
                && (window.location.pathname
                        === advancedMembershipLandingPathname
                    || getQueryParams().ref
                        === advancedMembershipLandingPathname)

        history.push(`/set-password?token=${
            submissionState.token}&refresh=${
            submissionState.refresh}${
                isAdvancedMembershipCheckout
                    ? '&advanced-membership-checkout=onboard'
                    : ''}`)
    }, [submissionState.success])

    const renderMemberFields = () => (
        <>
            <input
                type="hidden"
                name="approved"
                value="true"
            />
            <fieldset
                className="form-field margin-3-b"
                disabled={isDisabled()}
            >
                <legend>Which sounds most like you?*</legend>
                <div className="display-flex margin-1-t">
                    <input
                        id={`${id}-type-0`}
                        name="type"
                        type="radio"
                        value="Early stage"
                        required
                    />
                    <label
                        htmlFor={`${id}-type-0`}
                    >
                        Early stage
                    </label>
                </div>
                <div className="display-flex margin-1-t">
                    <input
                        id={`${id}-type-1`}
                        name="type"
                        type="radio"
                        value="MVP"
                        required
                    />
                    <label
                        htmlFor={`${id}-type-1`}
                    >
                        MVP
                    </label>
                </div>
                <div className="display-flex margin-1-t">
                    <input
                        id={`${id}-type-2`}
                        name="type"
                        type="radio"
                        value="Series A"
                        required
                    />
                    <label
                        htmlFor={`${id}-type-2`}
                    >
                        Series A
                    </label>
                </div>
                <p
                    id={`${id}-type-invalid`}
                    className="margin-1-t"
                    role="alert"
                >
                    A startup stage is required
                </p>
            </fieldset>
            <div className="form-field margin-3-b">
                <label
                    htmlFor={`${id}-vision`}
                    className="margin-1-b"
                >
                    Company vision*
                </label>
                <TextArea
                    id={`${id}-vision`}
                    name="vision"
                    autoCapitalize="sentences"
                    autoComplete="off"
                    spellCheck="true"
                    minLength="1"
                    maxLength={textareaMaxLength}
                    placeholder=" "
                    required
                    disabled={isDisabled()}
                />
                <p
                    id={`${id}-vision-invalid`}
                    role="alert"
                >
                    A company vision is required
                </p>
            </div>
        </>
    )

    const renderAdvisorFields = () => (
        <>
            <fieldset
                className="form-field margin-3-b"
                disabled={isDisabled()}
            >
                <legend>Which sounds most like you?*</legend>
                <div className="display-flex margin-1-t">
                    <input
                        id={`${id}-type-0`}
                        name="type"
                        type="radio"
                        value="Practitioner"
                        required
                    />
                    <label
                        htmlFor={`${id}-type-0`}
                    >
                        Practitioner
                    </label>
                </div>
                <div className="display-flex margin-1-t">
                    <input
                        id={`${id}-type-1`}
                        name="type"
                        type="radio"
                        value="Mentor"
                        required
                    />
                    <label
                        htmlFor={`${id}-type-1`}
                    >
                        Mentor
                    </label>
                </div>
                <div className="display-flex margin-1-t">
                    <input
                        id={`${id}-type-2`}
                        name="type"
                        type="radio"
                        value="Researcher"
                        required
                    />
                    <label
                        htmlFor={`${id}-type-2`}
                    >
                        Researcher
                    </label>
                </div>
                <p
                    role="alert"
                    id={`${id}-type-invalid`}
                >
                    An advisor type is required
                </p>
            </fieldset>
            <fieldset
                className="margin-1-b-md-up"
                disabled={isDisabled()}
            >
                <legend>Please rate your expertise in each of the following areas.</legend>
                <p
                    id={`${id}-expertise-rating-instructions`}
                    className="body-4"
                >
                    <em>5 is the highest rating.</em>
                </p>
                <div className="row pad-t">
                    {advisorRatings
                        .map((obj, idx) => Object.assign(obj, {
                            label: `${idx + 1}. ${obj.label}`
                        }))
                        .map((obj, idx) => (
                            <div
                                className="col-12 col-6-md-up margin-b margin-3-b-md-down"
                                key={`rating-${idx}`}
                            >
                                <Rating
                                    {...obj}
                                    id={`${id}-expertise-rating-${idx}`}
                                    describedby={`${id}-expertise-rating-instructions`}
                                />
                            </div>
                        ))}
                </div>
            </fieldset>
            <div className="form-field margin-3-b">
                <label
                    htmlFor={`${id}-qualifications`}
                    className="margin-1-b"
                >
                    Please tell us a bit about your experience*
                </label>
                <TextArea
                    id={`${id}-qualifications`}
                    name="qualifications"
                    autoCapitalize="sentences"
                    autoComplete="off"
                    spellCheck="true"
                    minLength="1"
                    maxLength={textareaMaxLength}
                    placeholder=" "
                    required
                    disabled={isDisabled()}
                />
                <p
                    id={`${id}-qualifications-invalid`}
                    role="alert"
                >
                    Your experience is required
                </p>
            </div>
        </>
    )

    return (
        <Form
            id={id}
            className="registration-form"
            onSubmit={onSubmit}
            disabled={isDisabled()}
            aria-describedby={submissionState.message
                && `${id}-submit-msg`}
        >
            <input
                type="hidden"
                name="role"
                value={role}
            />
            <div className="row-condensed margin-3-b">
                <div className="col-12 col-6-md-up margin-3-b-md-down">
                    <div className="form-field-floating">
                        <input
                            id={`${id}-first_name`}
                            name="first_name"
                            type="text"
                            autoComplete="given-name"
                            autoCapitalize="on"
                            spellCheck="false"
                            pattern=".{2,}$"
                            placeholder=" "
                            required
                            disabled={isDisabled()}
                        />
                        <label
                            htmlFor={`${id}-first_name`}
                        >
                            First name*
                        </label>
                        <p
                            id={`${id}-first_name-invalid`}
                            role="alert"
                        >
                            A first name is required
                        </p>
                    </div>
                </div>
                <div className="col-12 col-6-md-up">
                    <div className="form-field-floating">
                        <input
                            id={`${id}-last_name`}
                            name="last_name"
                            type="text"
                            autoComplete="family-name"
                            autoCapitalize="on"
                            spellCheck="false"
                            pattern=".+"
                            placeholder=" "
                            required
                            disabled={isDisabled()}
                        />
                        <label
                            htmlFor={`${id}-last_name`}
                        >
                            Last name*
                        </label>
                        <p
                            id={`${id}-last_name-invalid`}
                            role="alert"
                        >
                            A last name is required
                        </p>
                    </div>
                </div>
            </div>
            <div className="form-field-floating margin-3-b">
                <input
                    id={`${id}-email`}
                    name="email"
                    type="email"
                    autoComplete="on"
                    autoCapitalize="none"
                    spellCheck="false"
                    pattern="[a-zA-Z0-9\._%\+\-]+@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,}$"
                    placeholder=" "
                    required
                    disabled={isDisabled()}
                />
                <label
                    htmlFor={`${id}-email`}
                >
                    Email address*
                </label>
                <p
                    id={`${id}-email-invalid`}
                    role="alert"
                >
                    A valid email address is required
                </p>
            </div>
            <div className="form-field-floating margin-3-b">
                <input
                    id={`${id}-company`}
                    name="company"
                    type="text"
                    autoComplete="organization"
                    autoCapitalize="on"
                    spellCheck="false"
                    pattern=".{2,}"
                    placeholder=" "
                    required
                    disabled={isDisabled()}
                />
                <label
                    htmlFor={`${id}-company`}
                >
                    {role === 'member'
                        ? 'Company name'
                        : 'Place of work'}*
                </label>
                <p
                    id={`${id}-company-invalid`}
                    role="alert"
                >
                    A {role === 'member'
                        ? 'company name'
                        : 'place of work'} is required
                </p>
            </div>
            <div className="form-field-floating margin-3-b">
                <input
                    id={`${id}-linkedin`}
                    name="linkedin"
                    type="url"
                    autoComplete="on"
                    autoCapitalize="none"
                    spellCheck="false"
                    pattern="^(https*:\/\/)*(www\.)*linkedin\.com\/.+"
                    data-url-scheme="https"
                    placeholder=" "
                    disabled={isDisabled()}
                />
                <label
                    htmlFor={`${id}-linkedin`}
                >
                    LinkedIn profile URL
                </label>
                <p
                    id={`${id}-linked-invalid`}
                    role="alert"
                >
                    A valid linkedin.com URL is required and must start with https://
                </p>
            </div>
            {role === 'member'
                ? renderMemberFields()
                : renderAdvisorFields()}
            <div className="display-flex margin-3-t flex-justify-content-center-md">
                <button
                    className={`${props.submitBtnSecondaryStyle
                        ? 'btn-secondary '
                        : ''}btn-lg flex-grow-md-down pad-l-lg-up pad-r-lg-up`}
                    type="submit"
                    disabled={isDisabled()}
                >
                    {props.submitBtnText || 'Submit'}
                </button>
            </div>
            <p
                ref={submitMsg}
                id={`${id}-submit-msg`}
                className="margin-2-t text-align-center-md"
                role={submissionState.success
                    ? 'status'
                    : 'alert'}
                style={{
                    display: submissionState.success !== null
                        && 'block'
                }}
                tabIndex="-1"
            >
                {submissionState.message}
            </p>
        </Form>
    )
}
